/**
 * SurveyorAuditOverlay — DRR-309 Phase 9
 *
 * Read-only audit lens for compliance review.
 * Shows surveyors the decision trail and evidence chain.
 *
 * Key features:
 * - Side-by-side: requirement vs actual implementation
 * - Audit checklist with evidence attachment
 * - "Surveyor is asking about X" quick-pull
 * - Read-only mode (no edits allowed)
 */

import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  ThemeIcon,
  Badge,
  Tabs,
  Card,
  List,
  Accordion,
  Alert,
  Button,
  TextInput,
  Divider,
  Anchor,
  Progress,
  ScrollArea,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconClipboardCheck,
  IconSearch,
  IconFileText,
  IconScale,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconExternalLink,
  IconDownload,
  IconHistory,
  IconEye,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useJourneyOverlay } from './JourneyOverlayProvider';

// ============================================================================
// GraphQL Query
// ============================================================================

const JOURNEY_AUDIT_SECTIONS = gql`
  query JourneyAuditSections($tenantId: String!, $instanceId: String!) {
    journeyAuditSections(tenantId: $tenantId, instanceId: $instanceId) {
      id
      name
      description
      requirements {
        id
        citation
        description
        status
        evidenceCount
        notes
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface SurveyorAuditOverlayProps {
  /** Tenant ID for queries */
  tenantId?: string;
  /** Instance ID for queries */
  instanceId?: string;
  /** Subject being audited */
  subjectName?: string;
  /** Survey/audit type */
  auditType?: string;
  /** Callback when closed */
  onClose?: () => void;
}

export interface ComplianceRequirement {
  id: string;
  citation: string;
  requirement: string;
  evidence?: Evidence[];
  status: 'compliant' | 'non_compliant' | 'partial' | 'not_applicable';
  notes?: string;
}

export interface Evidence {
  id: string;
  type: 'document' | 'attestation' | 'observation' | 'record';
  title: string;
  timestamp: Date;
  signedBy?: string;
  documentUrl?: string;
}

export interface AuditSection {
  id: string;
  title: string;
  citation: string;
  requirements: ComplianceRequirement[];
}


// ============================================================================
// Status Config
// ============================================================================

const STATUS_CONFIG: Record<
  ComplianceRequirement['status'],
  { color: string; icon: React.ReactNode; label: string }
> = {
  compliant: {
    color: 'green',
    icon: <IconCheck size={14} />,
    label: 'Compliant',
  },
  non_compliant: {
    color: 'red',
    icon: <IconX size={14} />,
    label: 'Non-Compliant',
  },
  partial: {
    color: 'yellow',
    icon: <IconAlertCircle size={14} />,
    label: 'Partial',
  },
  not_applicable: {
    color: 'gray',
    icon: <IconX size={14} />,
    label: 'N/A',
  },
};

// ============================================================================
// Main Component
// ============================================================================

export function SurveyorAuditOverlay({
  tenantId,
  instanceId,
  subjectName = 'Subject',
  auditType = 'Survey',
  onClose,
}: SurveyorAuditOverlayProps) {
  const { isOverlayActive, deactivateOverlay } = useJourneyOverlay();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>('checklist');

  const isOpen = isOverlayActive('SurveyorAudit');

  // Fetch audit sections from backend
  const { data, loading } = useQuery(JOURNEY_AUDIT_SECTIONS, {
    variables: { tenantId: tenantId ?? '', instanceId: instanceId ?? '' },
    skip: !tenantId || !instanceId || !isOpen,
  });

  // Transform backend data to component types
  const auditSections: AuditSection[] = (data?.journeyAuditSections ?? []).map(
    (s: { id: string; name: string; description: string; requirements: Array<{ id: string; citation: string; description: string; status: string; evidenceCount: number; notes?: string }> }) => ({
      id: s.id,
      title: s.name,
      citation: s.description,
      requirements: s.requirements.map((r) => ({
        id: r.id,
        citation: r.citation,
        requirement: r.description,
        status: r.status as ComplianceRequirement['status'],
        notes: r.notes,
        evidence: r.evidenceCount > 0 ? new Array(r.evidenceCount).fill(null).map((_, i) => ({
          id: `${r.id}-ev-${i}`,
          type: 'document' as const,
          title: `Evidence ${i + 1}`,
          timestamp: new Date(),
        })) : undefined,
      })),
    })
  );

  // Calculate overall compliance
  const allRequirements = auditSections.flatMap((s) => s.requirements);
  const compliantCount = allRequirements.filter(
    (r) => r.status === 'compliant'
  ).length;
  const compliancePercent = allRequirements.length > 0
    ? Math.round((compliantCount / allRequirements.length) * 100)
    : 0;

  const handleClose = () => {
    onClose?.();
    deactivateOverlay('SurveyorAudit');
  };

  // Filter requirements by search
  const filteredSections = auditSections.map((section) => ({
    ...section,
    requirements: section.requirements.filter(
      (req) =>
        !searchQuery ||
        req.requirement.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.citation.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((section) => section.requirements.length > 0);

  if (loading) {
    return (
      <Modal opened={isOpen} onClose={handleClose} title="Compliance Audit" size="xl">
        <Center p="xl">
          <Stack align="center" gap="sm">
            <Loader size="lg" />
            <Text c="dimmed">Loading audit sections...</Text>
          </Stack>
        </Center>
      </Modal>
    );
  }

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <ThemeIcon color="violet" variant="light" size="lg">
            <IconClipboardCheck size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>Compliance Audit</Text>
            <Text size="xs" c="dimmed">
              {subjectName} — {auditType}
            </Text>
          </div>
        </Group>
      }
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        {/* Audit mode banner */}
        <Alert icon={<IconEye size={16} />} color="violet" variant="light">
          <Group justify="space-between">
            <Text size="sm">
              <strong>Read-Only Audit Mode</strong> — All data is view-only for
              compliance review
            </Text>
            <Badge variant="light" color="violet">
              {compliancePercent}% Compliant
            </Badge>
          </Group>
        </Alert>

        {/* Search */}
        <TextInput
          placeholder="Search requirements or citations..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab
              value="checklist"
              leftSection={<IconClipboardCheck size={14} />}
            >
              Checklist
            </Tabs.Tab>
            <Tabs.Tab value="evidence" leftSection={<IconFileText size={14} />}>
              Evidence
            </Tabs.Tab>
            <Tabs.Tab value="timeline" leftSection={<IconHistory size={14} />}>
              Decision Trail
            </Tabs.Tab>
          </Tabs.List>

          {/* Checklist Tab */}
          <Tabs.Panel value="checklist" pt="md">
            <Stack gap="md">
              {filteredSections.map((section) => (
                <AuditSectionCard key={section.id} section={section} />
              ))}
              {filteredSections.length === 0 && (
                <Alert color="gray" variant="light">
                  No requirements match your search
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Evidence Tab */}
          <Tabs.Panel value="evidence" pt="md">
            <EvidenceList sections={auditSections} />
          </Tabs.Panel>

          {/* Decision Trail Tab */}
          <Tabs.Panel value="timeline" pt="md">
            <DecisionTrail />
          </Tabs.Panel>
        </Tabs>

        {/* Actions */}
        <Divider />
        <Group justify="space-between">
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            color="violet"
          >
            Export Audit Report
          </Button>
          <Button variant="default" onClick={handleClose}>
            Close Audit View
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================================
// Audit Section Card
// ============================================================================

interface AuditSectionCardProps {
  section: AuditSection;
}

function AuditSectionCard({ section }: AuditSectionCardProps) {
  const compliantCount = section.requirements.filter(
    (r) => r.status === 'compliant'
  ).length;

  return (
    <Card padding="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <div>
            <Text fw={600}>{section.title}</Text>
            <Text size="xs" c="dimmed">
              {section.citation}
            </Text>
          </div>
          <Badge variant="light" color="blue">
            {compliantCount}/{section.requirements.length} Compliant
          </Badge>
        </Group>

        <Progress
          value={(compliantCount / section.requirements.length) * 100}
          color="green"
          size="sm"
        />

        <Accordion variant="separated" chevronPosition="left">
          {section.requirements.map((req) => (
            <RequirementItem key={req.id} requirement={req} />
          ))}
        </Accordion>
      </Stack>
    </Card>
  );
}

// ============================================================================
// Requirement Item
// ============================================================================

interface RequirementItemProps {
  requirement: ComplianceRequirement;
}

function RequirementItem({ requirement }: RequirementItemProps) {
  const statusConfig = STATUS_CONFIG[requirement.status];

  return (
    <Accordion.Item value={requirement.id}>
      <Accordion.Control>
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="sm" variant="light" color={statusConfig.color}>
            {statusConfig.icon}
          </ThemeIcon>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs">
              <Badge size="xs" variant="outline" color="gray">
                {requirement.citation}
              </Badge>
              <Badge size="xs" variant="light" color={statusConfig.color}>
                {statusConfig.label}
              </Badge>
            </Group>
            <Text size="sm" lineClamp={2}>
              {requirement.requirement}
            </Text>
          </div>
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        <Stack gap="sm">
          {/* Requirement vs Implementation side-by-side */}
          <Paper p="sm" bg="gray.0" radius="sm">
            <Group gap="md" align="flex-start">
              <div style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" fw={500}>
                  REQUIREMENT
                </Text>
                <Text size="sm">{requirement.requirement}</Text>
              </div>
              <Divider orientation="vertical" />
              <div style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" fw={500}>
                  IMPLEMENTATION
                </Text>
                <Text size="sm">
                  {requirement.notes || 'No implementation notes'}
                </Text>
              </div>
            </Group>
          </Paper>

          {/* Evidence list */}
          {requirement.evidence && requirement.evidence.length > 0 && (
            <div>
              <Text size="xs" fw={500} mb="xs">
                Evidence ({requirement.evidence.length})
              </Text>
              <List size="sm" spacing="xs">
                {requirement.evidence.map((ev) => (
                  <List.Item
                    key={ev.id}
                    icon={
                      <ThemeIcon size="xs" variant="light" color="blue">
                        <IconFileText size={10} />
                      </ThemeIcon>
                    }
                  >
                    <Group gap="xs">
                      <Text size="sm">{ev.title}</Text>
                      {ev.documentUrl && (
                        <Anchor size="xs" href={ev.documentUrl} target="_blank">
                          <IconExternalLink size={12} />
                        </Anchor>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {ev.signedBy} •{' '}
                      {new Date(ev.timestamp).toLocaleDateString()}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </div>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

// ============================================================================
// Evidence List
// ============================================================================

interface EvidenceListProps {
  sections: AuditSection[];
}

function EvidenceList({ sections }: EvidenceListProps) {
  const allEvidence = sections.flatMap((s) =>
    s.requirements.flatMap((r) =>
      (r.evidence || []).map((e) => ({
        ...e,
        requirement: r.requirement,
        citation: r.citation,
      }))
    )
  );

  // Group by type
  const byType = allEvidence.reduce(
    (acc, ev) => {
      if (!acc[ev.type]) acc[ev.type] = [];
      acc[ev.type]?.push(ev);
      return acc;
    },
    {} as Record<string, typeof allEvidence>
  );

  const typeLabels: Record<string, string> = {
    document: 'Documents',
    attestation: 'Attestations',
    observation: 'Observations',
    record: 'Records',
  };

  return (
    <Stack gap="md">
      {Object.entries(byType).map(([type, items]) => (
        <Card key={type} padding="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600}>{typeLabels[type] || type}</Text>
            <Badge variant="light">{items.length}</Badge>
          </Group>
          <Stack gap="xs">
            {items.map((ev) => (
              <Paper key={ev.id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {ev.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {ev.citation} • {ev.signedBy}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">
                      {new Date(ev.timestamp).toLocaleDateString()}
                    </Text>
                    {ev.documentUrl && (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconExternalLink size={12} />}
                      >
                        View
                      </Button>
                    )}
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

// ============================================================================
// Decision Trail
// ============================================================================

function DecisionTrail() {
  // Mock decision trail data
  const decisions = [
    {
      id: '1',
      timestamp: new Date('2026-05-10T09:15:00'),
      action: 'Service Plan Approved',
      actor: 'Mary Johnson, ALD',
      reason: 'All regulatory requirements met',
    },
    {
      id: '2',
      timestamp: new Date('2026-05-03T14:32:00'),
      action: 'Nursing Assessment Completed',
      actor: 'Jane Smith, RN',
      reason: '72-hour deadline compliance',
    },
    {
      id: '3',
      timestamp: new Date('2026-05-01T10:00:00'),
      action: 'Rights Delivered and Acknowledged',
      actor: 'System',
      reason: 'Admission workflow requirement',
    },
  ];

  return (
    <Stack gap="md">
      <Alert icon={<IconScale size={16} />} color="blue" variant="light">
        <Text size="sm">
          Decision trail shows the chronological sequence of compliance
          decisions and their justifications.
        </Text>
      </Alert>

      <Stack gap="xs">
        {decisions.map((decision, index) => (
          <Paper key={decision.id} p="sm" withBorder>
            <Group gap="md" wrap="nowrap">
              <ThemeIcon
                size="sm"
                variant="light"
                color={index === 0 ? 'blue' : 'gray'}
              >
                <IconCheck size={14} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    {decision.action}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {new Date(decision.timestamp).toLocaleString()}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed">
                  By: {decision.actor}
                </Text>
                <Text size="xs" c="blue">
                  Reason: {decision.reason}
                </Text>
              </div>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

// ============================================================================
// Compact Badge (for embedding in headers)
// ============================================================================

export function SurveyorAuditBadge() {
  const { activateOverlay } = useJourneyOverlay();

  return (
    <Badge
      size="sm"
      variant="light"
      color="violet"
      leftSection={<IconClipboardCheck size={12} />}
      style={{ cursor: 'pointer' }}
      onClick={() => activateOverlay('SurveyorAudit')}
    >
      Audit Mode
    </Badge>
  );
}

export default SurveyorAuditOverlay;
